import React, { useReducer, useContext, useEffect, useRef, useCallback } from 'react';
import { IFirmwareForAssign, IGetPaginatedFirmwareForAssign, IItemFirmwareForAssign, IAssignFirmware } from 'types';
import { firmwareTypes } from 'appConstants';
import { IAppContext, AppContext } from 'appState/appContext';
import { firmwareAssignmentReducer } from './firmwareAssignmentReducer';
import { FirmwareAssignmentActionTypes } from './firmwareAssignmentActions';
import {
	firmwareAssignmentInitialState,
	FirmwareAssignmentContext,
	TActiveItemToDisplay,
	TTypeOfModalWindow,
	TIDSelectedTypeFirmware,
	IIDsSelectedItems,
	IFirmwareWithAdditionalProperty,
} from './firmwareAssignmentContext';
import {
	getPaginatedFirmwareForAssign,
	getCustomersForAssignFirmware,
	getRestaurantsByIDsCustomersForFirmware,
	getRestaurantsByIDsRestaurantsForFirmware,
	assignFirmware,
} from 'backend/firmware';
import { useFetchData } from 'hooks/useFetchData';
import { AxiosResponse } from 'axios';
import { createAppError } from 'utils/helpers';

const FirmwareAssignmentState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(firmwareAssignmentReducer, firmwareAssignmentInitialState);
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<
		AxiosResponse<IFirmwareForAssign>,
		IGetPaginatedFirmwareForAssign
	>(isMounted.current ? getPaginatedFirmwareForAssign : null, {
		pageNumber: state.currentPage,
		pageSize: Number(state.countOfItems),
		searchByName: state.searchString,
		type: state.IDSelectedTypeFirmware,
		unassigned: state.isUnassignedFirmware ? state.isUnassignedFirmware : null,
	});

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: FirmwareAssignmentActionTypes.SET_FIRMWARE_FOR_ASSIGN,
				payload: {
					firmwareForAssign: response.data,
				},
			});
		}
	}, [response]);

	const changeSearchString = (value: string) => {
		dispatch({
			type: FirmwareAssignmentActionTypes.CHANGE_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	};

	const changeSearchStringForAllCustomers = useCallback((value: string) => {
		dispatch({
			type: FirmwareAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_ALL_CUSTOMERS,
			payload: {
				searchStringForAllCustomers: value,
			},
		});
	}, []);

	const changeSearchStringForRestaurants = useCallback((value: string) => {
		dispatch({
			type: FirmwareAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_RESTAURANTS,
			payload: {
				searchStringForRestaurants: value,
			},
		});
	}, []);

	const changeSearchStringForFirmwares = useCallback((value: string) => {
		dispatch({
			type: FirmwareAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_FIRMWARES,
			payload: {
				searchStringForFirmwares: value,
			},
		});
	}, []);

	const changeTypeFirmware = (value: TIDSelectedTypeFirmware) => {
		dispatch({
			type: FirmwareAssignmentActionTypes.CHANGE_TYPE_FIRMWARE,
			payload: {
				IDSelectedTypeFirmware: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: FirmwareAssignmentActionTypes.RESET_SELECTED_CONTROLS });

	const changeIsUnassignedFirmwareStatus = (value: boolean) => {
		dispatch({
			type: FirmwareAssignmentActionTypes.CHANGE_UNASSIGNED_FIRMWARE_STATUS,
			payload: {
				isUnassignedFirmware: value,
			},
		});
	};

	const setIDsSelectedFirmware = (value: string[]) => {
		dispatch({
			type: FirmwareAssignmentActionTypes.SET_IDS_SELECTED_FIRMWARE,
			payload: {
				IDsSelectedFirmware: value,
			},
		});
	};

	const changeActiveItemToDisplay = (value: TActiveItemToDisplay) => {
		dispatch({
			type: FirmwareAssignmentActionTypes.CHANGE_ACTIVE_ITEM_TO_DISPLAY,
			payload: {
				activeItemToDisplay: value,
			},
		});
	};

	const setShowBreadcrumbs = (value?: boolean) => {
		const isShowBreadcrumbs = value !== undefined ? value : !state.isShowBreadcrumbs;

		dispatch({
			type: FirmwareAssignmentActionTypes.SET_SHOW_BREADCRUMBS,
			payload: {
				isShowBreadcrumbs,
			},
		});
	};

	const setAllCustomers = async () => {
		try {
			showLoader();
			const response = await getCustomersForAssignFirmware();
			dispatch({
				type: FirmwareAssignmentActionTypes.SET_ALL_CUSTOMERS,
				payload: {
					allCustomers: response.data.customers,
				},
			});
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const setIDsSelectedCustomers = async (value: IIDsSelectedItems, isUpdateRestaurantsList: boolean) => {
		try {
			if (Object.keys(value).length === 0 && isUpdateRestaurantsList) {
				dispatch({
					type: FirmwareAssignmentActionTypes.SET_IDS_SELECTED_CUSTOMERS,
					payload: {
						IDsSelectedCustomers: {},
						restaurants: [],
						IDsSelectedRestaurants: {},
						firmwares: [],
					},
				});
			} else if (isUpdateRestaurantsList) {
				const arrWithCustomerIDs = Object.keys(value);
				showLoader();
				const response = await getRestaurantsByIDsCustomersForFirmware(arrWithCustomerIDs);
				const allRestaurantIDs = response.data.customers
					.filter((customer) => customer.restaurants.length)
					.map((customer) => customer.restaurants.map((restaurant) => restaurant.restaurantId))
					.flat();
				const updatedIDsSelectedRestaurants = allRestaurantIDs.reduce<IIDsSelectedItems>((acc, key) => {
					if (key in state.IDsSelectedRestaurants) {
						acc[key] = state.IDsSelectedRestaurants[key];
					}
					return acc;
				}, {});
				const updatedFirmwares = state.firmwares.filter((el) => allRestaurantIDs.includes(el.restaurantId));
				dispatch({
					type: FirmwareAssignmentActionTypes.SET_IDS_SELECTED_CUSTOMERS,
					payload: {
						IDsSelectedCustomers: value,
						restaurants: response.data.customers,
						IDsSelectedRestaurants: updatedIDsSelectedRestaurants,
						firmwares: updatedFirmwares,
					},
				});
			} else {
				// just to open and close tabs with restaurants.
				dispatch({
					type: FirmwareAssignmentActionTypes.SET_IDS_SELECTED_CUSTOMERS,
					payload: {
						IDsSelectedCustomers: value,
						restaurants: state.restaurants,
						IDsSelectedRestaurants: state.IDsSelectedRestaurants,
						firmwares: state.firmwares,
					},
				});
			}
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const setIDsSelectedRestaurants = async (value: IIDsSelectedItems, isUpdateFirmwaresList: boolean) => {
		try {
			if (Object.keys(value).length === 0 && isUpdateFirmwaresList) {
				dispatch({
					type: FirmwareAssignmentActionTypes.SET_IDS_SELECTED_RESTAURANTS,
					payload: {
						IDsSelectedRestaurants: {},
						firmwares: [],
					},
				});
			} else if (isUpdateFirmwaresList) {
				const arrWithRestaurantIDs = Object.keys(value);
				showLoader();
				const response = await getRestaurantsByIDsRestaurantsForFirmware(arrWithRestaurantIDs);
				const IDsRestaurantFromState = state.firmwares.map((el) => el.restaurantId);
				const IDsRestaurantFromResponse = response.data.restaurants.map((el) => el.restaurantId);
				const filteredFirmwaresFromState = state.firmwares.filter((el) =>
					IDsRestaurantFromResponse.includes(el.restaurantId),
				);
				const filteredFirmwaresFromResponse = response.data.restaurants
					.filter((el) => !IDsRestaurantFromState.includes(el.restaurantId))
					.map((el) => ({
						restaurantId: el.restaurantId,
						restaurantAddress: el.restaurantAddress,
						isChanged: false,
						firmwares: el.firmwares,
					}));
				dispatch({
					type: FirmwareAssignmentActionTypes.SET_IDS_SELECTED_RESTAURANTS,
					payload: {
						IDsSelectedRestaurants: value,
						firmwares: [...filteredFirmwaresFromState, ...filteredFirmwaresFromResponse],
					},
				});
			} else {
				// just to open and close tabs with firmwares.
				dispatch({
					type: FirmwareAssignmentActionTypes.SET_IDS_SELECTED_RESTAURANTS,
					payload: {
						IDsSelectedRestaurants: value,
						firmwares: state.firmwares,
					},
				});
			}
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const addSelectedFirmwareToRestaurant = () => {
		const fullDataOfSelectedFirmwares = state.IDsSelectedFirmware.reduce<IItemFirmwareForAssign[]>((acc, id) => {
			const searchFirmware = state.firmwareForAssign.items.find((el) => el.id === id);
			if (searchFirmware) {
				acc.push(searchFirmware);
			}
			return acc;
		}, []);

		if (fullDataOfSelectedFirmwares.length > 2) {
			dispatch({
				type: FirmwareAssignmentActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'firmwareLimit', // You can not transfer more than two firmware.
				},
			});
			return;
		} else if (
			fullDataOfSelectedFirmwares.length === 2 &&
			fullDataOfSelectedFirmwares[0].type === fullDataOfSelectedFirmwares[1].type
		) {
			dispatch({
				type: FirmwareAssignmentActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'firmwareOfSameType', // You can not transfer firmware with the same types.
				},
			});
			return;
		}

		const IDsOfOpenRestaurantTabs = Object.keys(state.IDsSelectedRestaurants).filter(
			(id) => state.IDsSelectedRestaurants[id] === true,
		);
		const fullDataOfOpenedRestaurantTabs = state.firmwares.filter((el) =>
			IDsOfOpenRestaurantTabs.includes(el.restaurantId),
		);

		if (fullDataOfOpenedRestaurantTabs.length) {
			if (
				fullDataOfOpenedRestaurantTabs.some(
					(restaurant) => fullDataOfSelectedFirmwares.length + restaurant.firmwares.length > 2,
				)
			) {
				dispatch({
					type: FirmwareAssignmentActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'firmwareLimit', // You can not transfer more than two firmware.
					},
				});
				return;
			} else if (
				fullDataOfOpenedRestaurantTabs.some(
					(restaurant) =>
						restaurant.firmwares[0] &&
						fullDataOfSelectedFirmwares[0].type === restaurant.firmwares[0].firmwareType,
				)
			) {
				const typeName = firmwareTypes.find(
					(el) => el.firmwareTypeID === fullDataOfSelectedFirmwares[0].type,
				)?.firmwareTypeName;
				dispatch({
					type: FirmwareAssignmentActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow:
							typeName === 'MAX' // You cannot transfer the type of firmware that is already in the restaurant.
								? 'restaurantAlreadyHasFirmwareTypeMAX'
								: 'restaurantAlreadyHasFirmwareTypeAndroid',
					},
				});
				return;
			}

			// console.log('All conditions for pressing the "assign" button are met.');
			const updatedFirmwares = state.firmwares.map((restaurant) => {
				if (!IDsOfOpenRestaurantTabs.includes(restaurant.restaurantId)) {
					return restaurant;
				}
				const firmwaresFromFullData = fullDataOfSelectedFirmwares.map((firmware) => ({
					firmwareId: firmware.id,
					firmwareName: firmware.name,
					restaurantAddress: null,
					firmwareType: firmware.type,
				}));
				return {
					restaurantId: restaurant.restaurantId,
					restaurantAddress: restaurant.restaurantAddress,
					isChanged: true,
					firmwares: [...restaurant.firmwares, ...firmwaresFromFullData],
				};
			});
			dispatch({
				type: FirmwareAssignmentActionTypes.SET_FIRMWARES,
				payload: {
					firmwares: updatedFirmwares,
					IDsSelectedFirmware: [],
				},
			});
		}
	};

	const deleteFirmwareFromRestaurant = (restaurantID: string, firmwareID: string) => {
		const updatedFirmwares = state.firmwares.reduce<IFirmwareWithAdditionalProperty[]>((acc, restaurant) => {
			if (restaurant.restaurantId === restaurantID) {
				const filteredFirmwares = restaurant.firmwares.filter((el) => el.firmwareId !== firmwareID);
				acc.push({
					restaurantId: restaurant.restaurantId,
					restaurantAddress: restaurant.restaurantAddress,
					isChanged: true,
					firmwares: filteredFirmwares,
				});
				return acc;
			}
			acc.push(restaurant);
			return acc;
		}, []);
		dispatch({
			type: FirmwareAssignmentActionTypes.SET_FIRMWARES,
			payload: {
				firmwares: updatedFirmwares,
				IDsSelectedFirmware: state.IDsSelectedFirmware,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: FirmwareAssignmentActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: FirmwareAssignmentActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const resetChanges = () => dispatch({ type: FirmwareAssignmentActionTypes.RESET_CHANGES });

	const sendChanges = async () => {
		try {
			const dataForAssign = state.firmwares.reduce<IAssignFirmware[]>((acc, restaurant) => {
				if (restaurant.isChanged) {
					acc.push({
						restaurantId: restaurant.restaurantId,
						firmwareIds: restaurant.firmwares.map((el) => el.firmwareId),
					});
				}
				return acc;
			}, []);
			showLoader();
			await assignFirmware(dataForAssign);
			changeTypeOfModalWindow('success');
		} catch (err) {
			changeTypeOfModalWindow('error');
		} finally {
			hideLoader();
		}
	};

	const updateModuleDataAfterSaving = () => {
		refetch();
		setAllCustomers();
	};

	useEffect(() => {
		isMounted.current = true;
		refetch();
	}, [
		state.currentPage,
		state.countOfItems,
		state.searchString,
		state.IDSelectedTypeFirmware,
		state.isUnassignedFirmware,
	]);

	useEffect(() => {
		if (state.activeItemToDisplay === 'firmware' && !state.firmwares.length) {
			changeActiveItemToDisplay('restaurants');
		} else if (!state.restaurants.length) {
			changeActiveItemToDisplay('customers');
		}
	}, [state.restaurants, state.firmwares]);

	useEffect(() => {
		setAllCustomers();
	}, []);

	return (
		<FirmwareAssignmentContext.Provider
			value={{
				changeSearchString,
				changeSearchStringForAllCustomers,
				changeSearchStringForRestaurants,
				changeSearchStringForFirmwares,
				changeTypeFirmware,
				resetSelectedControls,
				changeIsUnassignedFirmwareStatus,
				setIDsSelectedFirmware,
				changeActiveItemToDisplay,
				setShowBreadcrumbs,
				setIDsSelectedCustomers,
				setIDsSelectedRestaurants,
				addSelectedFirmwareToRestaurant,
				deleteFirmwareFromRestaurant,
				changeTypeOfModalWindow,
				changeCurrentPage,
				resetChanges,
				sendChanges,
				updateModuleDataAfterSaving,
				firmwareForAssign: state.firmwareForAssign,
				searchString: state.searchString,
				searchStringForAllCustomers: state.searchStringForAllCustomers,
				searchStringForRestaurants: state.searchStringForRestaurants,
				searchStringForFirmwares: state.searchStringForFirmwares,
				IDSelectedTypeFirmware: state.IDSelectedTypeFirmware,
				isUnassignedFirmware: state.isUnassignedFirmware,
				IDsSelectedFirmware: state.IDsSelectedFirmware,
				activeItemToDisplay: state.activeItemToDisplay,
				isShowBreadcrumbs: state.isShowBreadcrumbs,
				allCustomers: state.allCustomers,
				IDsSelectedCustomers: state.IDsSelectedCustomers,
				restaurants: state.restaurants,
				IDsSelectedRestaurants: state.IDsSelectedRestaurants,
				firmwares: state.firmwares,
				typeOfModalWindow: state.typeOfModalWindow,
				currentPage: state.currentPage,
				countOfItems: state.countOfItems,
			}}>
			{children}
		</FirmwareAssignmentContext.Provider>
	);
};

export default FirmwareAssignmentState;
